import { Box, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormHelperText, InputLabel, OutlinedInput, FormControlLabel, Stack, MenuItem, Select, SelectChangeEvent } from '@mui/material'
import { FunctionComponent, useEffect, useState, useContext } from 'react'
import { useFormik } from 'formik'
import * as Yup from 'yup'
import { DialogSaveButtons } from '../../../presentation-components/miscellaneous-components/dialog-save-buttons'
import AccreditationsService from '../../../../shared/services/accreditations/accreditations.service'
import { IAccreditation } from '../../../../shared/services/accreditations/accreditations.model'
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels'
import SpendBasedConversionService from '../../../../shared/services/spend-based-conversion/spend-based-conversion.service'
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service'
import { ISpendBasedConversion } from '../../../../shared/services/spend-based-conversion/spend-based-conversion.model'
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model'
import CustomFieldService from '../../../../shared/services/custom-field/custom-field.service'
import { ICustomField } from '../../../../shared/services/custom-field/custom-field.model'
import { IFileUploadLineSupplier } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model'
import moment from 'moment'
import { createDisplayFormatSpendBasedConversion } from '../../../../shared/utils/conversion-utils'
import { formatTransaction } from '../../../../shared/utils/format-post-utils'


export const EditUploadLineSupplierDialog: FunctionComponent<{
  editUploadLine: IFileUploadLineSupplier,
  open: boolean,
  closeHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IFileUploadLineSupplier) => void,
  isSubmitting: boolean,
  errorCode: number,
  errorMessage: string,
}> = ({
  editUploadLine,
  open,
  closeHandler,
  submitHandler,
  isSubmitting,
  errorCode,
  errorMessage
}) => {
    //Services
    const accreditationsService = new AccreditationsService()
    const conversionSourceService = new ConversionSourceService()
    const spendBasedConversionService = new SpendBasedConversionService()
    const customFieldService = new CustomFieldService()
    //Context
    const screenLabelsContext = useContext(ScreenLabelsContext)
    //State
    const [customFields, setCustomFields] = useState<ICustomField[]>()
    const [accreditations, setAccreditations] = useState<IAccreditation[]>()
    const [spendBasedConversions, setSpendBasedConversions] = useState<ISpendBasedConversion[]>()
    const [conversionSources, setConversionSources] = useState<IConversionSource[]>()

    const supplierSchema = Yup.object().shape({
      name: Yup.string().required(`You must provide a name for the ${screenLabelsContext.getLabel('supplier', { plural: false })}`),
      contactSupplierReference: Yup.string().nullable(),
      primaryContactName: Yup.string().nullable(),
      primaryContactEmail: Yup.string().test('email-format', 'Please enter a valid email address', function (value) {
        if (!value || value.trim() === '') {
          return true;
        }
        return Yup.string().email().isValidSync(value);
      }).nullable(),
      conversionSourceId: Yup.string().nullable(),
      spendBasedConversionId: Yup.string().test('no-spend-based-without-source', 'You must select a conversion source before selecting an activity', function (value) {
        const conversionSourceId = this.parent.conversionSourceId;
        if (value && !conversionSourceId) {
          return false;
        }
        return true;
      }).nullable(),
      haveCarbonReductionTargets: Yup.boolean(),
      targetsVerified: Yup.boolean(),
      verifiers: Yup.array().of(Yup.string()).when('targetsVerified', {
        is: true,
        then: (schema) => schema.test('at-least-one-verifier', 'Please select at least one verifier', function (value) {
          return Array.isArray(value) && value.length > 0;
        }).required('Please select at least one verifier'),
        otherwise: (schema) => schema.nullable(),
      }),
      ...(customFields && customFields.length > 0 ? customFields.reduce((acc, field) => {
        if (field.isMandatory) {
          acc[field.id] = Yup.string().required(`${field.fieldName} is mandatory`);
        } else {
          acc[field.id] = Yup.string().nullable();
        }
        return acc;
      }, {} as any) : {}),
    })

    const formik = useFormik<any>({
      initialValues: {
        name: editUploadLine.name,
        contactSupplierReference: editUploadLine.contactSupplierReference,
        haveCarbonReductionTargets: editUploadLine.haveCarbonReductionTargets,
        targetsVerified: editUploadLine.targetsVerified,
        verifiers: editUploadLine.verifiers,
        spendBasedConversionId: editUploadLine.spendBasedConversionId,
        conversionSourceId: editUploadLine.conversionSourceId,
        primaryContactName: editUploadLine.primaryContactName,
        primaryContactEmail: editUploadLine.primaryContactEmail,
      },
      validationSchema: supplierSchema,
      enableReinitialize: true,
      onSubmit: (values) => {
        const response = formatTransaction(values, '', true, customFields)
        submitHandler(editUploadLine.id, response)
      }
    })

    useEffect(() => {
      formik.setValues({
        name: editUploadLine.name,
        contactSupplierReference: editUploadLine.contactSupplierReference,
        haveCarbonReductionTargets: editUploadLine.haveCarbonReductionTargets,
        targetsVerified: editUploadLine.targetsVerified,
        verifiers: editUploadLine.verifiers,
        spendBasedConversionId: editUploadLine.spendBasedConversionId,
        conversionSourceId: editUploadLine.conversionSourceId,
        primaryContactName: editUploadLine.primaryContactName,
        primaryContactEmail: editUploadLine.primaryContactEmail,
      })
      accreditationsService.getAll()
        .then((response: any) => {
          const filtered = (response || []).filter((acc: IAccreditation) => acc.name !== 'Other')
          setAccreditations(filtered)
        })
        .catch((err) => {
          console.log(err)
        })
      conversionSourceService.getAll(true, 'spendBasedConversion')
        .then(response => {
          setConversionSources(response)
        })
        .catch(err => console.log(err))
      customFieldService.getAllActive(`&entryType=contactSupplier`)
        .then(response => {
          setCustomFields(response)
        })
        .catch(err => console.log(err))
    }, [editUploadLine])

    useEffect(() => {
      const existingSourceId = formik.values.conversionSourceId
      if (existingSourceId) {
        spendBasedConversionService
          .getSpendBasedConversionsForConversionSource(existingSourceId, moment().year().toString(), true, true)
          .then((response: any) => {
            const rows = Array.isArray(response) ? response : response?.rows
            const formattedRows = (rows || []).map((conversion: ISpendBasedConversion) =>
              createDisplayFormatSpendBasedConversion(conversion, 'localAuthorityCustomIdentifier', 'id', 'activity', 'Code {codeField}: {descriptionField}')
            ).sort((a: any, b: any) =>
              String(a.displayCode || '').localeCompare(String(b.displayCode || ''), undefined, { numeric: true, sensitivity: 'base' })
            )
            setSpendBasedConversions(formattedRows)
          })
          .catch(err => console.log(err))
      } else {
        setSpendBasedConversions([])
      }
    }, [formik.values.conversionSourceId])

    useEffect(() => {
      if (customFields && customFields.length && (editUploadLine as any)?.customFieldValues) {
        customFields.forEach(field => {
          const matchingValue = (editUploadLine as any).customFieldValues.find((v: any) => v.customFieldId === field.id)?.value ?? ''
          formik.setFieldValue(field.id, matchingValue, false)
        })
      }
    }, [customFields, editUploadLine])

    useEffect(() => {
      if (!accreditations) { return }
      const current = (formik.values.verifiers || []) as string[]
      if (!Array.isArray(current) || current.length === 0) { return }
      const validNames = new Set(accreditations.map(a => a.name))
      const filtered = current.filter(name => validNames.has(name))
      if (filtered.length !== current.length) {
        formik.setFieldValue('verifiers', filtered, false)
      }
    }, [accreditations, formik, formik.values.verifiers])

    const selectConversionSourceHandler = (e: SelectChangeEvent<string>) => {
      const conversionSourceId = e.target.value
      const previousSourceId = formik.values.conversionSourceId
      if (formik.values.spendBasedConversionId && previousSourceId && previousSourceId !== conversionSourceId) {
        formik.setFieldValue('spendBasedConversionId', null)
      }
      formik.setFieldValue('conversionSourceId', conversionSourceId)
      spendBasedConversionService.getSpendBasedConversionsForConversionSource(conversionSourceId, moment().year().toString(), true, true)
        .then((response: any) => {
          const rows = Array.isArray(response) ? response : response?.rows
          const formattedRows = (rows || []).map((conversion: ISpendBasedConversion) =>
            createDisplayFormatSpendBasedConversion(conversion, 'localAuthorityCustomIdentifier', 'id', 'activity', 'Code {codeField}: {descriptionField}')
          ).sort((a: any, b: any) =>
            String(a.displayCode || '').localeCompare(String(b.displayCode || ''), undefined, { numeric: true, sensitivity: 'base' })
          )
          setSpendBasedConversions(formattedRows)
        })
        .catch(err => console.log(err))
    }

    const cancelHandler = () => {
      formik.resetForm();
      closeHandler();
    }

    return (
      <Dialog open={open} onClose={closeHandler} maxWidth='xs'>
        <Box sx={{ p: 4 }} component='form'>
          <DialogTitle sx={{ pt: 0, pl: 0, pr: 0 }}>
            Edit Uploaded Data
          </DialogTitle>
          <DialogContent sx={{ pt: 0, pl: 0, pr: 0 }}>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='name'>{screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} Name</InputLabel>
              <OutlinedInput id='name' label={`${screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} Name`} name='name'
                value={formik.values.name || ''}
                onChange={formik.handleChange}
                error={formik.touched.name && Boolean(formik.errors.name)} />
              {formik.touched.name && formik.errors.name &&
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                  {formik.errors.name as string}
                </FormHelperText>
              }
              {(errorCode === 1 || errorCode === 2) && errorMessage &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{errorMessage}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='contactSupplierReference'>{screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} Reference</InputLabel>
              <OutlinedInput id='contactSupplierReference' label={`${screenLabelsContext.getLabel('supplier', { plural: false, capitalize: 'first' })} Reference`} name='contactSupplierReference'
                value={formik.values.contactSupplierReference || ''}
                onChange={formik.handleChange}
                error={formik.touched.contactSupplierReference && Boolean(formik.errors.contactSupplierReference)}
                sx={{ color: 'text.primary' }}></OutlinedInput>
              {formik.touched.contactSupplierReference && formik.errors.contactSupplierReference &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.contactSupplierReference as string}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={formik.values.haveCarbonReductionTargets}
                  name='haveCarbonReductionTargets'
                  onChange={formik.handleChange} />}
                label='Carbon Reduction Commitment'
              />
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <FormControlLabel
                control={<Checkbox checked={formik.values.targetsVerified}
                  name='targetsVerified'
                  onChange={(e) => {
                    const isChecked = e.target.checked
                    formik.setFieldValue('targetsVerified', isChecked)
                    if (!isChecked) {
                      formik.setFieldValue('verifiers', [])
                    }
                  }} />}
                label='Targets Verification'
              />
            </FormControl>
            {formik.values.targetsVerified && accreditations && (
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel id='verifiers-label'>Verifiers</InputLabel>
                <Select
                  labelId='verifiers-label'
                  id='verifiers'
                  multiple
                  value={(formik.values.verifiers || []) as string[]}
                  onChange={(e) => {
                    const value = e.target.value as string[]
                    formik.setFieldValue('verifiers', value)
                  }}
                  input={<OutlinedInput label='Verifiers' />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {(selected as string[]).map((value) => (
                        <Chip key={value} label={value} size='small' />
                      ))}
                    </Box>
                  )}
                  sx={{ color: 'text.primary' }}
                >
                  {(accreditations || []).map((acc) => (
                    <MenuItem key={acc.id} value={acc.name}>
                      <Checkbox checked={((formik.values.verifiers || []) as string[]).includes(acc.name)} />
                      {acc.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.verifiers && formik.errors.verifiers &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.verifiers as any}</FormHelperText>
                }
              </FormControl>
            )}
            {conversionSources &&
              <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
                <InputLabel htmlFor='conversionSourceId'>Default Conversion Factor Source</InputLabel>
                <Select
                  id='conversionSourceId' label='Default Conversion Factor Source' name='conversionSourceId'
                  value={formik.values.conversionSourceId || ''}
                  onChange={selectConversionSourceHandler}
                  error={formik.touched.conversionSourceId && Boolean(formik.errors.conversionSourceId)}
                  sx={{ color: 'text.primary', }}>
                  {conversionSources.map(conversionSource => (
                    <MenuItem key={conversionSource.id} value={conversionSource.id}>{conversionSource.source}</MenuItem>
                  ))}
                </Select>
                {formik.touched.conversionSourceId && formik.errors.conversionSourceId &&
                  <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.conversionSourceId as string}</FormHelperText>
                }
              </FormControl>}
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='spendBasedConversionId'>Default Spend Activity</InputLabel>
              <Select
                id='spendBasedConversionId'
                label='Default Spend Activity'
                name='spendBasedConversionId'
                value={formik.values.spendBasedConversionId || ''}
                onChange={formik.handleChange}
                error={formik.touched.spendBasedConversionId && Boolean(formik.errors.spendBasedConversionId)}
                disabled={!formik.values.conversionSourceId}
                sx={{ color: 'text.primary' }}>
                {(spendBasedConversions || []).map(conversion => {
                  const label = (conversion as any).displayText || (conversion as any).activity
                  return <MenuItem key={conversion.id} value={conversion.id}>{label}</MenuItem>
                })}
              </Select>
              {formik.touched.spendBasedConversionId && formik.errors.spendBasedConversionId &&
                <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>{formik.errors.spendBasedConversionId as string}</FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='primaryContactName'>Primary Contact Name</InputLabel>
              <OutlinedInput id='primaryContactName' label='Primary Contact Name' name='primaryContactName'
                value={formik.values.primaryContactName || ''}
                error={formik.touched.primaryContactName && Boolean(formik.errors.primaryContactName)}
                onChange={formik.handleChange} />
              {formik.touched.primaryContactName && formik.errors.primaryContactName &&
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                  {formik.errors.primaryContactName as string}
                </FormHelperText>
              }
            </FormControl>
            <FormControl fullWidth variant='outlined' sx={{ mt: 2 }}>
              <InputLabel htmlFor='primaryContactEmail'>Primary Contact Email</InputLabel>
              <OutlinedInput id='primaryContactEmail' label='Primary Contact Email' name='primaryContactEmail'
                value={formik.values.primaryContactEmail || ''}
                onChange={formik.handleChange}
                error={formik.touched.primaryContactEmail && Boolean(formik.errors.primaryContactEmail)} />
              {formik.touched.primaryContactEmail && formik.errors.primaryContactEmail &&
                <FormHelperText sx={{ color: '#bf3333', marginLeft: '16px !important' }}>
                  {formik.errors.primaryContactEmail as string}
                </FormHelperText>
              }
            </FormControl>
            {customFields && customFields.length > 0 && customFields.map((customField, index) => {
              return (
                <Stack direction='row' spacing={2} key={index} sx={{ mt: 2 }}>
                  <FormControl fullWidth variant='outlined'>
                    <InputLabel htmlFor={customField.id}> {customField.fieldName}</InputLabel>
                    <OutlinedInput id={customField.id} label={customField.fieldName} name={customField.id}
                      type={customField.fieldType === 'number' ? 'number' : 'text'}
                      value={formik.values[customField.id]}
                      onChange={formik.handleChange}
                      error={formik.touched[customField.id] && Boolean(formik.errors[customField.id])}
                      sx={{ color: 'text.primary' }}></OutlinedInput>
                  </FormControl>
                  {formik.touched[customField.id] && formik.errors[customField.id] &&
                    <FormHelperText sx={{ color: "#bf3333", marginLeft: "16px !important" }}>
                      {formik.errors[customField.id] ? <div>{formik.errors[customField.id] as string}</div> : null}
                    </FormHelperText>
                  }
                </Stack>
              )
            })}
          </DialogContent>
          <DialogActions sx={{ p: 0 }}>
            <DialogSaveButtons
              cancelHandler={cancelHandler}
              submitHandler={formik.handleSubmit}
              isSubmitting={isSubmitting}
            />
          </DialogActions>
        </Box>
      </Dialog>
    )
  }


